import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import { CurrencyInput } from "./CurrencyInput";
import "./warranty-settings.css";

type Settings = {
  mode: "own" | "acquired" | "both";
  own_default_months: number;
  own_terms: string;
  own_public_page_enabled: boolean;
  require_warranty_selection_on_sale: boolean;
};
type Provider = {
  id: string;
  name: string;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
  active: boolean;
};
type Product = {
  id: string;
  provider_id: string;
  name: string;
  duration_months: number;
  cost_cents: number;
  coverage: string;
  active: boolean;
};
const errorText = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível guardar.";

export function WarrantySettings({
  organizationId,
  canManage,
}: {
  organizationId: string;
  canManage: boolean;
}) {
  const [settings, setSettings] = useState<Settings | null>(null),
    [providers, setProviders] = useState<Provider[]>([]),
    [products, setProducts] = useState<Product[]>([]),
    [activeSection, setActiveSection] = useState<"settings" | "providers" | "products">("settings"),
    [notice, setNotice] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  const load = async () => {
    const { data, error } = await supabase.rpc("after_sales_panel", {
      p_organization_id: organizationId,
    });
    if (error) return setNotice(error.message);
    setSettings(data.settings);
    setProviders(data.providers ?? []);
    setProducts(data.products ?? []);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  const saveSettings = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!settings) return;
    setBusy(true);
    const f = new FormData(e.currentTarget);
    const { error } = await supabase.rpc("save_warranty_settings", {
      p_organization_id: organizationId,
      p_mode: f.get("mode"),
      p_own_default_months: Number(f.get("months")),
      p_own_terms: f.get("terms"),
      p_public_page: f.get("public") === "on",
      p_require_on_sale: f.get("required") === "on",
    });
    setNotice(error?.message ?? "Configuração de garantias guardada.");
    if (!error) await load();
    setBusy(false);
  };
  const saveProvider = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    const f = new FormData(e.currentTarget);
    const { error } = await supabase.rpc("save_warranty_provider", {
      p_organization_id: organizationId,
      p_provider_id: null,
      p_name: f.get("name"),
      p_tax_id: f.get("tax"),
      p_email: f.get("email"),
      p_phone: f.get("phone"),
      p_active: true,
    });
    setNotice(error?.message ?? "Garantidora guardada.");
    if (!error) {
      e.currentTarget.reset();
      await load();
    }
    setBusy(false);
  };
  const saveProduct = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    const f = new FormData(e.currentTarget);
    try {
      const { error } = await supabase.rpc("save_warranty_product", {
        p_organization_id: organizationId,
        p_product_id: null,
        p_provider_id: f.get("provider"),
        p_name: f.get("name"),
        p_duration_months: Number(f.get("months")),
        p_cost_cents: Math.round(Number(f.get("cost")) * 100),
        p_coverage: f.get("coverage"),
        p_active: true,
      });
      if (error) throw error;
      setNotice("Produto de garantia guardado.");
      e.currentTarget.reset();
      await load();
    } catch (error) {
      setNotice(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  if (!settings) return <p>A preparar as garantias…</p>;
  return (
    <div className="warranty-settings">
      {notice && (
        <button
          type="button"
          className="stock-notice"
          onClick={() => setNotice(null)}
        >
          {notice} ×
        </button>
      )}
      <div className="warranty-subtabs" role="tablist" aria-label="Áreas de garantias">
        {([
          ["settings", "Configuração"],
          ["providers", "Garantidoras"],
          ["products", "Produtos"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeSection === key}
            className={activeSection === key ? "active" : ""}
            onClick={() => setActiveSection(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {activeSection === "settings" && (
      <form onSubmit={saveSettings} className="warranty-settings-form">
        <header className="warranty-panel-heading">
          <strong>Regras gerais das garantias</strong>
          <p>Defina os tipos utilizados pelo stand e as condições aplicadas por padrão.</p>
        </header>
        <label>
          <span>Garantias utilizadas</span>
          <select
            name="mode"
            defaultValue={settings.mode}
            disabled={!canManage}
          >
            <option value="own">Garantia própria</option>
            <option value="acquired">Garantia adquirida</option>
            <option value="both">Própria e adquirida</option>
          </select>
        </label>
        <label>
          <span>Duração padrão da garantia própria</span>
          <input
            name="months"
            type="number"
            min="1"
            max="120"
            defaultValue={settings.own_default_months}
            disabled={!canManage}
          />
          <small>Indique o número de meses aplicado por padrão.</small>
        </label>
        <label className="wide">
          <span>Condições da garantia própria</span>
          <textarea
            name="terms"
            defaultValue={settings.own_terms}
            disabled={!canManage}
          />
        </label>
        <label className="plain-check">
          <input
            name="required"
            type="checkbox"
            defaultChecked={settings.require_warranty_selection_on_sale}
            disabled={!canManage}
          />
          Exigir a escolha da garantia na venda
        </label>
        <label className="plain-check">
          <input
            name="public"
            type="checkbox"
            defaultChecked={settings.own_public_page_enabled}
            disabled={!canManage}
          />
          Disponibilizar as condições no site do stand
        </label>
        {canManage && (
          <button className="stock-primary" disabled={busy}>
            Guardar
          </button>
        )}
      </form>
      )}
      <div className="warranty-catalog">
        {activeSection === "providers" && <section>
          <header className="warranty-panel-heading"><strong>Garantidoras</strong><p>Consulte as entidades existentes ou adicione uma nova garantidora.</p></header>
          <div className="warranty-record-list" aria-label="Garantidoras registadas">
            {providers.length === 0 && <p className="warranty-record-empty">Ainda não existem garantidoras registadas.</p>}
            {providers.map((p) => (
              <article className="warranty-record-card" key={p.id}>
                <div className="warranty-record-identity">
                  <span className="warranty-record-mark" aria-hidden="true">{p.name.slice(0, 1).toUpperCase()}</span>
                  <span><strong>{p.name}</strong><small>{p.active ? "Garantidora ativa" : "Garantidora inativa"}</small></span>
                </div>
                <dl>
                  <div><dt>NIF</dt><dd>{p.tax_id || "Não indicado"}</dd></div>
                  <div><dt>Email</dt><dd>{p.email || "Não indicado"}</dd></div>
                  <div><dt>Telefone</dt><dd>{p.phone || "Não indicado"}</dd></div>
                </dl>
              </article>
            ))}
          </div>
          {canManage && (
            <form onSubmit={saveProvider} className="warranty-entry-form">
              <label><span>Nome da garantidora</span><input name="name" required /></label>
              <label><span>NIF</span><input name="tax" /></label>
              <label><span>Email</span><input name="email" type="email" /></label>
              <label><span>Telefone</span><input name="phone" /></label>
              <button className="stock-primary" disabled={busy}>
                Guardar
              </button>
            </form>
          )}
        </section>}
        {activeSection === "products" && <section>
          <header className="warranty-panel-heading"><strong>Produtos</strong><p>Consulte os produtos existentes ou associe um novo produto a uma garantidora.</p></header>
          <div className="warranty-record-list" aria-label="Produtos de garantia registados">
            {products.length === 0 && <p className="warranty-record-empty">Ainda não existem produtos de garantia registados.</p>}
            {products.map((p) => (
              <article className="warranty-record-card" key={p.id}>
                <div className="warranty-record-identity">
                  <span className="warranty-record-mark" aria-hidden="true">{p.name.slice(0, 1).toUpperCase()}</span>
                  <span><strong>{p.name}</strong><small>{p.active ? "Produto ativo" : "Produto inativo"}</small></span>
                </div>
                <dl>
                  <div><dt>Garantidora</dt><dd>{providers.find((provider) => provider.id === p.provider_id)?.name || "Não identificada"}</dd></div>
                  <div><dt>Duração</dt><dd>{p.duration_months} meses</dd></div>
                  <div><dt>Custo</dt><dd>{(p.cost_cents / 100).toLocaleString("pt-PT", { style: "currency", currency: "EUR" })}</dd></div>
                </dl>
                {p.coverage && <p className="warranty-record-note"><strong>Cobertura</strong>{p.coverage}</p>}
              </article>
            ))}
          </div>
          {canManage && (
            <form onSubmit={saveProduct} className="warranty-entry-form warranty-product-form">
              <label><span>Garantidora</span><select name="provider" required defaultValue="">
                <option value="" disabled>
                  Escolher garantidora
                </option>
                {providers
                  .filter((p) => p.active)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select></label>
              <label><span>Nome do produto</span><input name="name" required /></label>
              <label><span>Duração em meses</span><input
                name="months"
                type="number"
                min="1"
                max="120"
                required
              /></label>
              <label><span>Custo</span>
            <CurrencyInput
              currencyCode="EUR"
              name="cost"
                type="number"
                min="0"
                step="0.01"
                required
              />
              </label>
              <label className="wide"><span>Cobertura e condições</span><textarea
                name="coverage"
                required
              /></label>
              <button className="stock-primary" disabled={busy}>
                Guardar
              </button>
            </form>
          )}
        </section>}
      </div>
    </div>
  );
}
